// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

import Configuration from '../../config/einvoicing-configuration';
import { DocumentInfoPayload } from './types';

const API_BASE = `${Configuration.eventServiceUrl}/dashboard`;

/**
 * A single validation error returned by the backend validate-only endpoint.
 */
export interface BackendValidationError {
    code: string;
    message: string;
}

/**
 * Response shape of POST /dashboard/ValidateManualInvoice.
 */
export interface ManualInvoiceValidationResult {
    isValid: boolean;
    errors: BackendValidationError[];
    // Production ingestion checks the backend dry run deliberately does NOT run (e.g. go-live date
    // gating, supplier routing). A valid result means "content + France business rules passed", not
    // "guaranteed to be accepted", so the UI surfaces these as a caveat.
    checksSkipped: string[];
}

/**
 * Validates a manual invoice payload against the REAL backend France e-invoicing rules
 * (the same EdicomFranceValidator the processing pipeline runs) without persisting or
 * submitting it. This gives a hard guarantee that a payload reported as valid will be
 * accepted for processing.
 *
 * Throws on transport/authorization failures so the caller can block payload generation
 * rather than silently falling back to client-only validation.
 *
 * @param payload The DocumentInfo payload produced by the form.
 * @param accessToken Bearer token for the dashboard API.
 */
export async function validateManualInvoicePayload(
    payload: DocumentInfoPayload,
    accessToken: string
): Promise<ManualInvoiceValidationResult> {
    const response = await fetch(`${API_BASE}/ValidateManualInvoice`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(`Validation service returned ${response.status} ${response.statusText}`.trim());
    }

    const result = (await response.json()) as ManualInvoiceValidationResult;
    // Defensive normalization: guarantee the caller always gets arrays to iterate.
    return {
        isValid: Boolean(result?.isValid),
        errors: Array.isArray(result?.errors) ? result.errors : [],
        checksSkipped: Array.isArray(result?.checksSkipped) ? result.checksSkipped : [],
    };
}
